import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TableContainer,
  Td,
  ThCheck,
  TdCheck,
  NetDot,
  SkeletonBox,
} from "./styles";
import Header from "./header";
import HoverTooltip from "./HoverTooltip";

const ToText = (value: any) => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return Object.values(value).join(" ");
  return String(value);
};

const getRowKey = (row: any, idx: number) => row?.id ?? row?.name ?? idx;

const SKELETON_ROWS = 8;

const Table = ({
  tags,
  data,
  isCheck = false,
  setCheckData,
  loading = false,
}) => {
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const [hoverTarget, setHoverTarget] = useState<HTMLElement | null>(null);
  const [hoverItems, setHoverItems] = useState<string[]>([]);

  /* ---------------- filtering ---------------- */

  const filtered = useMemo(() => {
    const entries = Object.entries(filters).filter(([, v]) => v);
    if (!entries.length) return safeData;

    return safeData.filter((row) =>
      entries.every(([k, v]) =>
        ToText(row[k]).toLowerCase().includes(String(v).toLowerCase())
      )
    );
  }, [safeData, filters]);

  /* ---------------- sorting ---------------- */

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortAsc
        ? String(av).localeCompare(String(bv), undefined, { numeric: true })
        : String(bv).localeCompare(String(av), undefined, { numeric: true });
    });
    return list;
  }, [filtered, sortKey, sortAsc]);

  /* ---------------- ✅ 선택된 row 계산 ---------------- */

  const selectedRows = useMemo(() => {
    const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
    return sorted.filter((row, idx) =>
      selectedKeys.includes(String(getRowKey(row, idx)))
    );
  }, [selected, sorted]);

  /* ---------------- ✅ 부모로 선택 결과 전달 ---------------- */

  useEffect(() => {
    setCheckData(selectedRows);
  }, [selectedRows, setCheckData]);

  /* ---------------- header checkbox ---------------- */

  const visibleKeys = sorted.map(getRowKey);
  const allChecked =
    visibleKeys.length && visibleKeys.every((k) => selected[k]);
  const someChecked = visibleKeys.some((k) => selected[k]) && !allChecked;

  const headerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.indeterminate = someChecked;
    }
  }, [someChecked]);

  return (
    <>
      <TableContainer>
        <colgroup>
          {isCheck && <col style={{ width: "42px" }} />}
          {tags.map((tag) => (
            <col key={tag.name} style={{ width: tag.width }} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {isCheck && (
              <ThCheck>
                <input
                  ref={headerRef}
                  type="checkbox"
                  checked={!!allChecked}
                  disabled={loading}
                  onChange={() => {
                    const next = { ...selected };
                    visibleKeys.forEach((k) =>
                      allChecked ? delete next[k] : (next[k] = true)
                    );
                    setSelected(next);
                  }}
                />
              </ThCheck>
            )}

            {tags.map((tag) => (
              <Header
                tagData={tag}
                sortKey={sortKey}
                sortAsc={sortAsc}
                onSetSortDir={(k, asc) => {
                  setSortKey(k);
                  setSortAsc(asc);
                }}
                filterValue={filters[tag.name] ?? ""}
                onChangeFilter={(k, v) =>
                  setFilters((p) => ({ ...p, [k]: v || undefined }))
                }
                isOpen={openKey === tag.name}
                onToggleOpen={setOpenKey}
                onRequestClose={() => setOpenKey(null)}
              />
            ))}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <tr key={i}>
                {isCheck && (
                  <TdCheck>
                    <SkeletonBox />
                  </TdCheck>
                )}
                {tags.map((tag) => (
                  <Td key={tag.name}>
                    <SkeletonBox />
                  </Td>
                ))}
              </tr>
            ))
          ) : sorted.length === 0 ? (
            <tr>
              <Td
                colSpan={tags.length + (isCheck ? 1 : 0)}
                style={{ padding: 40 }}
              >
                데이터가 없습니다.
              </Td>
            </tr>
          ) : (
            sorted.map((row, idx) => {
              const key = getRowKey(row, idx);

              return (
                <tr key={key} data-selected={selected[key] ? "true" : "false"}>
                  {isCheck && (
                    <TdCheck>
                      <input
                        type="checkbox"
                        checked={!!selected[key]}
                        onChange={() =>
                          setSelected((p) => ({
                            ...p,
                            [key]: !p[key],
                          }))
                        }
                      />
                    </TdCheck>
                  )}

                  {tags.map((tag) => {
                    const value = row?.[tag.name];

                    if (tag.type === "power") {
                      return (
                        <Td key={tag.name}>
                          <NetDot $on={!!value} />
                        </Td>
                      );
                    }

                    if (Array.isArray(value)) {
                      return (
                        <Td key={tag.name}>
                          <span
                            onMouseEnter={(e) => {
                              setHoverTarget(e.currentTarget);
                              setHoverItems(value.map(String));
                            }}
                            onMouseLeave={() => setHoverTarget(null)}
                            style={{ cursor: "default", fontWeight: 500 }}
                          >
                            {value.length}
                          </span>
                        </Td>
                      );
                    }

                    return <Td key={tag.name}>{value ?? "-"}</Td>;
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </TableContainer>

      {hoverTarget && <HoverTooltip target={hoverTarget} items={hoverItems} />}
    </>
  );
};

export default Table;
